/*
	Önerilen filtre çiplerini tek satıra indirir.

	Sabit bir çip sayısı vermek yerine satırın gerçek genişliği ölçülüyor:
	çip metinleri "Nü sanatı" ile "Sahne sanatları ve sinema" arasında çok
	değiştiği için sabit sayı 1440px'te seyrek, 768-1024 arasında üç satır
	oluyordu. Ölçüm yaklaşımı her genişlikte tam bir satır bırakır.

	"..." düğmesi de aynı satıra sığacak şekilde hesaba katılır, böylece
	daralmış hâlde toplam yükseklik tek satırdır. Açılırken yükseklik
	animasyonla büyür; ürün ızgarası birden zıplamaz. Çipler her zaman
	listenin baştan bir parçasıdır, açılınca sıra değişmez.
*/
class InlinePillRow extends HTMLElement {
	connectedCallback() {
		this.list = this.querySelector(".facets-inline-pills");
		this.moreButton = this.querySelector("[data-pill-more]");
		if (!this.list || !this.moreButton) return;

		this.expanded = false;
		this.pills = Array.from(
			this.list.querySelectorAll(".facet-inline-pill:not(.facet-inline-pill--more)")
		);
		if (this.pills.length === 0) return;

		this.moreLabel = this.moreButton.querySelector("[data-pill-more-label]");
		this.labelMore = this.moreLabel ? this.moreLabel.textContent.trim() : "";
		this.labelLess =
			this.moreButton.getAttribute("data-label-less") || this.labelMore;

		this.onClick = this.onClick.bind(this);
		this.onResize = this.onResize.bind(this);
		this.moreButton.addEventListener("click", this.onClick);

		this.list.classList.add("facets-inline-pills--collapsible");

		if (typeof ResizeObserver === "function") {
			this.observer = new ResizeObserver(this.onResize);
			this.observer.observe(this.closest(".facets-drawer-head") || this);
		} else {
			window.addEventListener("resize", this.onResize);
		}

		this.layout();
	}

	disconnectedCallback() {
		if (this.observer) this.observer.disconnect();
		window.removeEventListener("resize", this.onResize);
	}

	availableWidth() {
		const head = this.closest(".facets-drawer-head");
		if (head) return head.clientWidth;
		const main = this.closest(".facets-container-main");
		if (main) return main.clientWidth;
		return this.clientWidth;
	}

	onResize() {
		// Ölçüm çekmece başlığının tam genişliğine bağlı; küçülen çip
		// satırı ResizeObserver'ı döngüye sokmasın diye liste genişliği
		// değil o sabit kapsayıcı kullanılıyor.
		const width = this.availableWidth();
		if (width === this.lastWidth) return;
		this.lastWidth = width;
		if (this.resizeTimer) clearTimeout(this.resizeTimer);
		this.resizeTimer = setTimeout(() => {
			this.expanded = false;
			this.list.style.height = "";
			this.layout();
		}, 120);
	}

	/*
		Masaüstünde tek, mobilde iki satır bırakılır. 390px'te tek satıra
		yalnızca iki çip sığıyor; "Özel boyutlu baskılar" gibi uzun etiketler
		satırı tek başına yiyor ve öneri satırı işe yaramaz hâle geliyordu.
		İki satır orada dört-beş çip gösterir, blok yine kısa kalır.
	*/
	get maxRows() {
		return window.matchMedia("(max-width: 749px)").matches ? 2 : 1;
	}

	/* İzin verilen satır sayısına kaç çip sığdığını ölçer, gerisini gizler. */
	layout() {
		const list = this.list;
		const maxRows = this.maxRows;
		const pills = this.pills;
		const moreButton = this.moreButton;

		if (pills.length === 0) return;

		const available = this.availableWidth();
		const prevWidth = list.style.width;
		const prevMaxWidth = list.style.maxWidth;
		if (available > 0) {
			list.style.width = available + "px";
			list.style.maxWidth = available + "px";
		}

		this.collapsedRailWidth = 0;
		moreButton.style.marginInlineStart = "";

		// Unhide all pills temporarily to read geometry
		pills.forEach((pill) => pill.removeAttribute("hidden"));
		moreButton.removeAttribute("hidden");

		const top1 = pills[0].offsetTop;
		const row2Pill = pills.find((p) => p.offsetTop > top1);
		const top2 = row2Pill ? row2Pill.offsetTop : top1;
		const maxAllowedTop = maxRows === 2 ? top2 : top1;

		const rowBeyondPill = maxRows === 2 ? pills.find((p) => p.offsetTop > top2) : row2Pill;
		const allFit = !rowBeyondPill;

		if (allFit) {
			this.visibleCount = pills.length;
			moreButton.setAttribute("hidden", "");
			list.classList.remove("facets-inline-pills--collapsed");
			list.classList.add("facets-inline-pills--expanded");
			list.style.width = prevWidth;
			list.style.maxWidth = prevMaxWidth;
			this.alignSortToDots();
			return;
		}

		let lastAllowedIdx = pills.findIndex((p) => p.offsetTop > maxAllowedTop) - 1;
		if (lastAllowedIdx < 0) lastAllowedIdx = 0;

		pills.forEach((p, idx) => {
			if (idx > lastAllowedIdx) p.setAttribute("hidden", "");
			else p.removeAttribute("hidden");
		});

		this.visibleCount = lastAllowedIdx + 1;
		this.syncMoreCount();

		while (lastAllowedIdx > 0 && moreButton.offsetTop > maxAllowedTop) {
			pills[lastAllowedIdx].setAttribute("hidden", "");
			lastAllowedIdx--;
			this.visibleCount = lastAllowedIdx + 1;
			this.syncMoreCount();
		}

		list.style.width = prevWidth;
		list.style.maxWidth = prevMaxWidth;
		this.collapse(false);
	}

	syncMoreCount() {
		const hidden = Math.max(0, this.pills.length - (this.visibleCount || 0));
		if (window.AlaMoreChip && typeof window.AlaMoreChip.setCount === "function") {
			window.AlaMoreChip.setCount(this.moreButton, hidden);
		} else if (this.moreButton) {
			this.moreButton.setAttribute("data-ala-more-count", String(hidden));
			const glyph = this.moreButton.querySelector(".facet-inline-pill__more-glyph");
			if (glyph) glyph.textContent = "+" + hidden;
		}
	}

	/*
		Sırala sağ kenarı, "(n)…" (veya yoksa son çip) sağ kenarına
		oturur. Satır genişliği koleksiyona göre değişir; sütunun
		sağına kilitlemek yanlış. Açıkken sort zıplamasın diye dar
		hâldeki ölçü saklanır.
	*/
	alignSortToDots() {
		const toolbar = this.closest(".facets-inline-suggested-toolbar");
		if (!toolbar) return;
		const actions = toolbar.querySelector(
			".facets-inline-suggested-toolbar__actions"
		);
		if (!actions) return;

		if (window.matchMedia("(max-width: 749px)").matches) {
			toolbar.style.setProperty("--suggested-sort-rail", "100%");
			return;
		}

		if (this.expanded && this.collapsedRailWidth > 0) {
			toolbar.style.setProperty(
				"--suggested-sort-rail",
				this.collapsedRailWidth + "px"
			);
			return;
		}

		const moreHidden =
			!this.moreButton || this.moreButton.hasAttribute("hidden");
		const lastPill = this.pills
			.filter((pill) => !pill.hasAttribute("hidden"))
			.pop();
		const anchor = moreHidden ? lastPill : this.moreButton;
		if (!anchor) {
			toolbar.style.setProperty("--suggested-sort-rail", "100%");
			return;
		}

		const anchorRight =
			Math.round(
				(anchor.getBoundingClientRect().right -
					toolbar.getBoundingClientRect().left) *
					100
			) / 100;
		const rail = this.railWidth(toolbar, actions, anchorRight);
		this.collapsedRailWidth = rail;
		toolbar.style.setProperty("--suggested-sort-rail", rail + "px");
	}

	/*
		Sırala sağ kenarı "..." ile hizalı kalır. Kalan çip sola düşünce
		ölçü o kadar daralmaz; buton, rozet ve cümle için yer bırakılır.
	*/
	railWidth(toolbar, actions, anchorRight) {
		const toolbarWidth = toolbar.getBoundingClientRect().width;
		const button = actions.querySelector(".facets__button-show--more");
		const sort = actions.querySelector(".facets-sorting");
		const buttonW = button ? button.getBoundingClientRect().width : 0;
		const sortW = sort ? sort.getBoundingClientRect().width : 0;
		const minText = 240;
		const gaps = 48;
		const minRail = buttonW + sortW + minText + gaps;
		return (
			Math.round(Math.min(toolbarWidth, Math.max(anchorRight, minRail)) * 100) /
			100
		);
	}

	collapse(animate) {
		const list = this.list;
		const from = list.getBoundingClientRect().height;

		this.pills.forEach((pill, index) => {
			if (index >= this.visibleCount) {
				pill.setAttribute("hidden", "");
			} else {
				pill.removeAttribute("hidden");
			}
		});
		this.moreButton.removeAttribute("hidden");
		this.moreButton.setAttribute("aria-expanded", "false");
		this.syncMoreCount();
		if (this.moreLabel) this.moreLabel.textContent = this.labelMore;
		list.classList.remove("facets-inline-pills--expanded");
		list.classList.add("facets-inline-pills--collapsed");
		this.expanded = false;

		this.animateHeight(from, animate);
		this.alignSortToDots();
	}

	expand() {
		const list = this.list;
		const from = list.getBoundingClientRect().height;

		this.pills.forEach((pill) => pill.removeAttribute("hidden"));
		this.moreButton.setAttribute("aria-expanded", "true");
		if (this.moreLabel) this.moreLabel.textContent = this.labelLess;
		list.classList.remove("facets-inline-pills--collapsed");
		list.classList.add("facets-inline-pills--expanded");
		this.expanded = true;

		this.animateHeight(from, true);
		this.alignSortToDots();
	}

	/* Yükseklik değişimini yumuşatır; bittiğinde auto'ya bırakır. */
	animateHeight(from, animate) {
		const list = this.list;
		list.style.height = "";
		const to = list.getBoundingClientRect().height;

		if (
			!animate ||
			from === to ||
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			return;
		}

		list.style.height = from + "px";
		// Tarayıcının başlangıç yüksekliğini uygulaması için tek kare bekle.
		requestAnimationFrame(() => {
			list.style.transition = "height 260ms ease";
			list.style.height = to + "px";
			const done = () => {
				list.style.transition = "";
				list.style.height = "";
				list.removeEventListener("transitionend", done);
			};
			list.addEventListener("transitionend", done);
			setTimeout(done, 400);
		});
	}

	onClick(event) {
		event.preventDefault();
		if (this.expanded) {
			this.collapse(true);
		} else {
			this.expand();
		}
	}
}

if (!customElements.get("inline-pill-row")) {
	customElements.define("inline-pill-row", InlinePillRow);
}
